import { useEffect, useState } from "react";

import { MainLayout } from "~/layout/MainLayout";
import { CheckIcon } from "~/shared/components/icons/CheckIcon";

declare global {
  interface Window {
    fbq: (
      action: string,
      eventName: string,
      params?: Record<string, any>,
    ) => void;
    _fbq: any;
  }
}

export const TestPixelScreen = () => {
  const [pixelInitialized, setPixelInitialized] = useState(false);
  const [pageViewFired, setPageViewFired] = useState(false);
  const [completeRegistrationFired, setCompleteRegistrationFired] =
    useState(false);

  useEffect(() => {
    // Check if pixel is already initialized (prevents duplicates)
    if (window.fbq && window._fbq) {
      console.log("Facebook Pixel already initialized, tracking PageView");
      window.fbq("track", "PageView");
      setPixelInitialized(true);
      setPageViewFired(true);
      return;
    }

    // Facebook Pixel base code
    (function (
      f: any,
      b: Document,
      e: string,
      v: string,
      n?: any,
      t?: any,
      s?: any,
    ) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.com/en_US/fbevents.js",
    );

    // Replace 'YOUR_PIXEL_ID' with your actual Facebook Pixel ID
    // Or better yet, get it from environment variable
    const PIXEL_ID = "1195244635827086"; // Your pixel ID

    window.fbq("init", PIXEL_ID);
    window.fbq("track", "PageView");

    setPixelInitialized(true);
    setPageViewFired(true);

    console.log(`Facebook Pixel initialized with ID: ${PIXEL_ID}`);
    console.log("PageView event tracked");
  }, []);

  const handleTestCompleteRegistration = () => {
    if (!window.fbq) {
      console.error("Facebook Pixel not initialized");
      return;
    }

    window.fbq("track", "CompleteRegistration", {
      value: 0.0,
      currency: "USD",
      content_name: "Test Registration from Staging",
    });

    setCompleteRegistrationFired(true);
    console.log("CompleteRegistration event fired");
  };

  return (
    <MainLayout>
      <div className="mx-auto max-w-2xl rounded-lg bg-white p-8 shadow-xl">
        <div className="mb-8 text-center">
          <h1 className="font-display text-large-title mb-4">
            Facebook Pixel Test Page
          </h1>
          <p className="text-gray-600">
            This page tests the Facebook Pixel integration in an iframe context
          </p>
        </div>

        {/* Status Section */}
        <div className="mb-8 space-y-4 rounded-lg bg-gray-50 p-6">
          <h2 className="mb-4 text-xl font-semibold text-gray-800">
            Pixel Status
          </h2>

          <div className="flex items-center gap-3">
            <div
              className={`flex h-6 w-6 items-center justify-center rounded-full ${
                pixelInitialized ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              {pixelInitialized && <CheckIcon className="h-4 w-4 text-white" />}
            </div>
            <span className="font-medium text-gray-700">
              Pixel Initialized: {pixelInitialized ? "Yes" : "Loading..."}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div
              className={`flex h-6 w-6 items-center justify-center rounded-full ${
                pageViewFired ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              {pageViewFired && <CheckIcon className="h-4 w-4 text-white" />}
            </div>
            <span className="font-medium text-gray-700">
              PageView Event: {pageViewFired ? "Fired" : "Waiting..."}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div
              className={`flex h-6 w-6 items-center justify-center rounded-full ${
                completeRegistrationFired ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              {completeRegistrationFired && (
                <CheckIcon className="h-4 w-4 text-white" />
              )}
            </div>
            <span className="font-medium text-gray-700">
              CompleteRegistration Event:{" "}
              {completeRegistrationFired ? "Fired" : "Not Fired"}
            </span>
          </div>
        </div>

        {/* Test Button */}
        <div className="mb-8 text-center">
          <button
            onClick={handleTestCompleteRegistration}
            disabled={!pixelInitialized || completeRegistrationFired}
            className="rounded-lg bg-green-500 px-8 py-4 font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {completeRegistrationFired
              ? "✓ Event Fired Successfully"
              : "Fire CompleteRegistration Event"}
          </button>
        </div>

        {/* Instructions */}
        <div className="rounded-lg border-2 border-yellow-200 bg-yellow-50 p-6">
          <h3 className="mb-3 font-semibold text-yellow-900">
            Testing Instructions
          </h3>
          <ol className="list-inside list-decimal space-y-2 text-sm text-yellow-800">
            <li>Install the Facebook Pixel Helper Chrome extension</li>
            <li>
              Load this page in your production WordPress iframe at:{" "}
              <code className="rounded bg-yellow-100 px-1 py-0.5">
                /test-pixel
              </code>
            </li>
            <li>
              Verify PageView event fires automatically (check the extension
              icon)
            </li>
            <li>Click the button above to fire CompleteRegistration event</li>
            <li>
              Confirm both events appear in Facebook Events Manager (may take a
              few minutes)
            </li>
            <li>Check browser console for detailed logging</li>
          </ol>
        </div>

        {/* Environment Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          Environment: {import.meta.env.MODE || "development"}
        </div>
      </div>
    </MainLayout>
  );
};
